import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CircleAlert, Info } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import useUser from '../../../hooks/useUser';
import { getErrorMessage } from '../../../utils/errorMessage';
import { Alert, Button } from '../../../design-system';
import AccountSettingsLoadingState from '../AccountSettingsLoadingState';
import UpgradeOfferSummary, {
  type UpgradeOfferPlan,
} from '../../../components/UpgradeOfferSummary';
import { navigateToCheckoutOverlay } from '../../../utils/checkoutOverlayNavigation';
import {
  getAccountSettingsUserCacheKey,
  makeAccountSettingsCacheKey,
  readAccountSettingsCache,
  writeAccountSettingsCache,
} from '../accountSettingsDataCache';
import './UsageSettings.css';

type ModelUsageRow = {
  model: string;
  events: number;
  inputTokens: number;
  outputTokens: number;
  cachedInputTokens: number;
  uncachedInputTokens: number;
  cacheWriteInputTokens: number;
  estimatedCostUsd: number;
};

type BonusCategory = 'purchased' | 'gifted' | 'legacy';

type BonusGrant = {
  id: string;
  category: BonusCategory;
  grantedNanoUsd: string;
  remainingNanoUsd: string;
  grantedUsd: string;
  remainingUsd: string;
  createdAt: string;
};

type CreditPack = {
  slug: string;
  name: string;
  creditUsd: string;
  priceCents: number | null;
  currency: string | null;
  available: boolean;
};

const BONUS_LABELS = {
  purchased: 'Bought by you',
  gifted: 'Gifted by BotSpot',
  legacy: 'Older bonus credits',
} satisfies Record<BonusCategory, string>;

const BONUS_ORDER: BonusCategory[] = ['purchased', 'gifted', 'legacy'];

type UsageSummaryResponse = {
  cycleStartDate: string | null;
  cycleEndDate: string | null;
  limits: {
    maxMessagesPerMonth: number;
    maxStrategyRevisionsPerMonth: number;
    maxBacktestsPerMonth: number;
    maxBacktestMinutesPerMonth: number;
    maxBots: number;
  };
  upgrade?: {
    currentTier?: { productName: string };
    resetDate?: string;
    productName: string | null;
    checkoutUrl: string | null;
    contactSalesUrl?: string | null;
    monthlyAiRetailCreditUsd?: string | null;
    plans?: UpgradeOfferPlan[];
    terminal?: boolean;
  } | null;
  usage: {
    messagesUsed: number;
    messagesRemaining: number;
    revisionsUsed: number;
    revisionsRemaining: number;
    backtestsUsed: number;
    backtestMinutesUsed: number;
    backtestMinutesRemaining: number | null;
    deploymentRuntimeMinutesUsed: number;
    ai: {
      eventCount: number;
      inputTokens: number;
      outputTokens: number;
      cachedInputTokens: number;
      uncachedInputTokens: number;
      cacheWriteInputTokens: number;
      estimatedCostUsd: number;
      byModel: ModelUsageRow[];
      allowanceNanoUsd: string;
      usedNanoUsd: string;
      remainingNanoUsd: string;
      permanentRemainingNanoUsd: string;
      percentageUsed: number;
      displayMode: 'percentage_only' | 'percentage_and_retail_debug';
      permanentBySource?: Record<
        BonusCategory,
        { grantedNanoUsd: string; remainingNanoUsd: string }
      >;
      permanentGrants?: BonusGrant[];
      totalAvailableNanoUsd?: string;
      spendOrder?: 'monthly_first';
      monthlyExhausted?: boolean;
      outOfCredits?: boolean;
      display: {
        allowanceUsd: string;
        usedUsd: string;
        remainingUsd: string;
        permanentRemainingUsd: string;
        totalAvailableUsd?: string;
      };
      bySourceNanoUsd: {
        agent: string;
        strategy: string;
      };
    };
  };
  message?: string;
};

const formatNumber = (value: number | null | undefined): string => {
  if (value == null || !Number.isFinite(value)) return 'n/a';
  return new Intl.NumberFormat('en-US').format(value);
};

const formatUsd = (value: number | null | undefined): string => {
  if (value == null || !Number.isFinite(value)) return 'n/a';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(value);
};

const formatRetailUsd = (value: number | null | undefined): string => {
  if (value == null || !Number.isFinite(value)) return 'n/a';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const formatPackPrice = (cents: number, currency: string | null): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: (currency || 'usd').toUpperCase(),
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);

const formatLimit = (value: number | null | undefined): string => {
  if (value == null) return 'n/a';
  if (value < 0) return 'n/a';
  return formatNumber(value);
};

const nanoUsdToNumber = (value: string | null | undefined): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed / 1_000_000_000 : 0;
};

const clampPercentage = (value: number | null | undefined): number => {
  if (value == null || !Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, value));
};

const formatDate = (iso: string | null): string => {
  if (!iso) return 'n/a';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'n/a';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
};

type UsageSource = 'agent' | 'strategy';

// Plain-English meaning of each usage source. Customers do not know what
// "AI Agent" or "Strategies" means on a bill, so each row explains itself.
const USAGE_SOURCE_INFO = {
  agent: {
    label: 'AI Agent',
    text: 'AI you use when you chat with the BotSpot AI. For example, asking it to build, change, or explain a strategy.',
  },
  strategy: {
    label: 'Strategies',
    text: 'AI your strategies use while they run on their own. For example, a bot that asks AI to read the news before it trades.',
  },
} satisfies Record<UsageSource, { label: string; text: string }>;

type UsageSourceLabelProps = {
  source: UsageSource;
  open: boolean;
  onToggle: (source: UsageSource) => void;
};

// Tap or click opens the note (works on phones); hover and keyboard focus open
// it too through CSS, so desktop users can just point at the icon.
const UsageSourceLabel = ({
  source,
  open,
  onToggle,
}: UsageSourceLabelProps): React.ReactElement => {
  const { label, text } = USAGE_SOURCE_INFO[source];
  const tipId = `usage-source-info-${source}`;
  return (
    <span className={`usage-source-label${open ? ' is-open' : ''}`}>
      <span>{label}</span>
      <button
        type="button"
        className="usage-source-info"
        aria-label={`What is ${label} usage?`}
        aria-expanded={open}
        aria-describedby={tipId}
        onClick={() => onToggle(source)}
      >
        <Info size={13} aria-hidden="true" />
      </button>
      <span id={tipId} role={open ? 'tooltip' : undefined} className="usage-source-tip">
        {text}
      </span>
    </span>
  );
};

const UsageSettings = (): React.ReactElement => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getApiToken, isAuthenticated, authLoading, user } = useUser();
  const apiBaseUrl = process.env.REACT_APP_API_URL || 'api';
  const cacheKey = makeAccountSettingsCacheKey(
    'usage-summary',
    getAccountSettingsUserCacheKey(user),
    apiBaseUrl,
  );
  const cached = readAccountSettingsCache<UsageSummaryResponse>(cacheKey);
  const [summary, setSummary] = useState<UsageSummaryResponse | null>(() => cached || null);
  const [loading, setLoading] = useState(() => !cached);
  const [error, setError] = useState('');

  const loadUsage = useCallback(
    async (options: { force?: boolean } = {}) => {
      if (!isAuthenticated || authLoading) return;
      if (!options.force) {
        const existing = readAccountSettingsCache<UsageSummaryResponse>(cacheKey);
        if (existing) {
          setSummary(existing);
          setLoading(false);
          return;
        }
      }

      setLoading(true);
      setError('');
      try {
        const token = await getApiToken();
        const response = await fetch(`${apiBaseUrl}/agent/usage-summary`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        // SAFETY: The authenticated endpoint is validated immediately through the fields read below.
        const data = (await response.json()) as UsageSummaryResponse;
        if (!response.ok) {
          throw new Error(data.message || 'Could not load usage summary.');
        }
        writeAccountSettingsCache(cacheKey, data);
        setSummary(data);
      } catch (err) {
        setError(getErrorMessage(err, 'Could not load usage summary.'));
      } finally {
        setLoading(false);
      }
    },
    [apiBaseUrl, authLoading, cacheKey, getApiToken, isAuthenticated],
  );

  useEffect(() => {
    void loadUsage();
  }, [loadUsage]);

  const [packs, setPacks] = useState<CreditPack[] | null>(null);
  const [packsError, setPacksError] = useState(false);
  const [buyingSlug, setBuyingSlug] = useState<string | null>(null);
  const [buyError, setBuyError] = useState('');
  const [openSourceInfo, setOpenSourceInfo] = useState<UsageSource | null>(null);
  const toggleSourceInfo = (source: UsageSource): void =>
    setOpenSourceInfo((current) => (current === source ? null : source));
  const [purchaseNotice, setPurchaseNotice] = useState<{
    variant: 'success' | 'info';
    text: string;
  } | null>(null);
  const confirmedSessionRef = useRef<string | null>(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const locationSearch = location?.search ?? '';
  const locationPathname = location?.pathname ?? '/account-settings/usage';

  const authedFetch = useCallback(
    async (path: string, init: RequestInit = {}) => {
      const token = await getApiToken();
      return fetch(`${apiBaseUrl}${path}`, {
        ...init,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
    },
    [apiBaseUrl, getApiToken],
  );

  useEffect(() => {
    if (!isAuthenticated || authLoading) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await authedFetch('/agent/ai-credit-packs');
        // SAFETY: The authenticated endpoint is validated immediately through the fields read below.
        const data = (await response.json()) as { packs?: CreditPack[] };
        if (!response.ok || !Array.isArray(data.packs)) throw new Error('packs unavailable');
        if (!cancelled) setPacks(data.packs);
      } catch {
        // No fallback prices: without the database catalog there is nothing to buy.
        if (!cancelled) setPacksError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authLoading, authedFetch, isAuthenticated]);

  // The buyer's return from Stripe. Confirm grants the credit right away, even
  // if the webhook has not arrived yet; both paths grant the same purchase once.
  useEffect(() => {
    if (!isAuthenticated || authLoading) return;
    const params = new URLSearchParams(locationSearch);
    const outcome = params.get('credits');
    if (!outcome) return;
    const clearQuery = () => navigate(locationPathname, { replace: true });
    if (outcome === 'canceled') {
      setPurchaseNotice({ variant: 'info', text: 'Checkout closed. You were not charged.' });
      clearQuery();
      return;
    }
    const sessionId = params.get('credit_session');
    if (outcome !== 'success' || !sessionId || confirmedSessionRef.current === sessionId) return;
    confirmedSessionRef.current = sessionId;
    // The ref (not an effect-local flag) guards this: StrictMode runs the
    // effect twice, and cancelling the first run would leave the purchase
    // unconfirmed because the ref already blocks the second.
    (async () => {
      for (let attempt = 0; attempt < 6 && mountedRef.current; attempt += 1) {
        try {
          const response = await authedFetch('/agent/ai-credit-packs/confirm', {
            method: 'POST',
            body: JSON.stringify({ sessionId }),
          });
          // SAFETY: The authenticated endpoint is validated immediately through the fields read below.
          const data = (await response.json()) as {
            status?: string;
            creditUsd?: string | null;
            message?: string;
          };
          if (!response.ok) throw new Error(data.message || 'Could not confirm your purchase.');
          if (data.status === 'granted') {
            await loadUsage({ force: true });
            if (mountedRef.current) {
              setPurchaseNotice({
                variant: 'success',
                text: `Payment received. We added $${data.creditUsd} of AI credit. It never expires.`,
              });
              clearQuery();
            }
            return;
          }
          if (mountedRef.current) {
            setPurchaseNotice({
              variant: 'info',
              text: 'Your payment is still going through. Your credits will show up here in a minute.',
            });
          }
        } catch (err) {
          if (mountedRef.current) {
            setPurchaseNotice({
              variant: 'info',
              text: getErrorMessage(
                err,
                'We could not confirm your purchase yet. Refresh in a minute.',
              ),
            });
          }
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, 5_000));
      }
    })();
  }, [
    authLoading,
    authedFetch,
    isAuthenticated,
    loadUsage,
    locationPathname,
    locationSearch,
    navigate,
  ]);

  const buyPack = async (pack: CreditPack) => {
    setBuyError('');
    setBuyingSlug(pack.slug);
    try {
      const response = await authedFetch('/agent/ai-credit-packs/checkout', {
        method: 'POST',
        body: JSON.stringify({ productSlug: pack.slug }),
      });
      // SAFETY: The authenticated endpoint is validated immediately through the fields read below.
      const data = (await response.json()) as { checkoutUrl?: string; message?: string };
      if (!response.ok || !data.checkoutUrl) {
        throw new Error(data.message || 'Could not open checkout.');
      }
      window.location.assign(data.checkoutUrl);
    } catch (err) {
      setBuyError(getErrorMessage(err, 'Could not open checkout.'));
      setBuyingSlug(null);
    }
  };

  if (authLoading || (loading && !summary)) {
    return <AccountSettingsLoadingState label="Usage" text="Loading this billing cycle." />;
  }

  const usage = summary?.usage;
  const limits = summary?.limits;
  const ai = usage?.ai;
  const aiPercentage = clampPercentage(ai?.percentageUsed);
  const aiAllowanceNanoUsd = nanoUsdToNumber(ai?.allowanceNanoUsd);
  const hasAiAllowance = aiAllowanceNanoUsd > 0;
  const showRetailDebug = ai?.displayMode === 'percentage_and_retail_debug';
  const sourceDisplay = (sourceNanoUsd: string | null | undefined): string => {
    const sourceUsed = nanoUsdToNumber(sourceNanoUsd);
    return formatRetailUsd(sourceUsed);
  };
  const bonusBySource = BONUS_ORDER.map((category) => ({
    category,
    granted: nanoUsdToNumber(ai?.permanentBySource?.[category]?.grantedNanoUsd),
    remaining: nanoUsdToNumber(ai?.permanentBySource?.[category]?.remainingNanoUsd),
  }));
  const bonusRemaining = nanoUsdToNumber(ai?.permanentRemainingNanoUsd);
  const bonusEverGranted = bonusRemaining > 0 || bonusBySource.some((source) => source.granted > 0);
  const totalAvailable =
    ai?.totalAvailableNanoUsd != null
      ? nanoUsdToNumber(ai.totalAvailableNanoUsd)
      : nanoUsdToNumber(ai?.remainingNanoUsd) + bonusRemaining;
  const hasCreditBuckets = hasAiAllowance || bonusEverGranted;
  const monthlyExhausted = ai?.monthlyExhausted ?? (hasAiAllowance && aiPercentage >= 100);
  // Out of credits means every bucket is empty, never just this month's plan.
  const isAiExhausted = hasCreditBuckets && (ai?.outOfCredits ?? totalAvailable <= 0);
  const usingBonusNow = monthlyExhausted && !isAiExhausted;
  const shouldWarnAi = hasAiAllowance && aiPercentage >= 80 && !monthlyExhausted;
  const resetLabel = formatDate(summary?.cycleEndDate ?? null);
  const grants = [...(ai?.permanentGrants ?? [])].reverse();
  const backtestPercentage =
    limits?.maxBacktestMinutesPerMonth && limits.maxBacktestMinutesPerMonth > 0
      ? clampPercentage(
          ((usage?.backtestMinutesUsed ?? 0) / limits.maxBacktestMinutesPerMonth) * 100,
        )
      : 0;

  return (
    <div className="usage-settings" data-testid="usage-settings">
      <div className="settings-subpage-header">
        <div className="settings-subpage-header-left">
          <h2 className="settings-subpage-title">Usage</h2>
          <p className="settings-subpage-description">
            This billing cycle. Resets {formatDate(summary?.cycleEndDate ?? null)}.
          </p>
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => {
            void loadUsage({ force: true });
          }}
        >
          Refresh
        </Button>
      </div>

      {error ? (
        <Alert variant="danger" className="usage-settings-error">
          {error}
        </Alert>
      ) : null}

      {purchaseNotice ? (
        <Alert variant={purchaseNotice.variant} className="usage-purchase-notice">
          {purchaseNotice.text}
          {purchaseNotice.variant === 'success'
            ? ` You now have ${formatRetailUsd(totalAvailable)} available.`
            : null}
        </Alert>
      ) : null}

      {shouldWarnAi ? (
        <Alert variant="warning" className="usage-settings-warning">
          {Math.round(aiPercentage)}% of this month’s plan credits used. They reset {resetLabel}.
          {bonusRemaining > 0 ? ' Your bonus credits kick in after that.' : ''}
        </Alert>
      ) : null}
      <section
        className={`usage-credit-card${isAiExhausted ? ' usage-credit-card--exhausted' : ''}${shouldWarnAi ? ' usage-credit-card--warning' : ''}`}
        aria-label="AI credit usage"
      >
        <div className="usage-credit-heading">
          <div>
            <span className="usage-metric-label">
              {isAiExhausted && summary?.upgrade?.currentTier?.productName
                ? `Current plan: ${summary.upgrade.currentTier.productName}`
                : hasCreditBuckets
                  ? 'AI credits'
                  : 'AI activity'}
            </span>
            <strong className="usage-credit-percent">
              {isAiExhausted ? (
                <>
                  <CircleAlert size={24} aria-hidden="true" />
                  You’re out of AI credits
                </>
              ) : hasCreditBuckets ? (
                `${formatRetailUsd(totalAvailable)} available now`
              ) : (
                `${formatNumber(ai?.eventCount)} AI calls`
              )}
            </strong>
            {isAiExhausted ? (
              <span className="usage-credit-balance">
                Plan credits come back {resetLabel}. Buy credits below to keep going now.
              </span>
            ) : hasCreditBuckets ? (
              <span className="usage-credit-balance">
                We use your plan credits first. Bonus credits kick in after that.
              </span>
            ) : null}
          </div>
          {!hasCreditBuckets && (ai?.eventCount ?? 0) > 0 ? (
            <span className="usage-credit-debug">
              {formatUsd(ai?.estimatedCostUsd)} provider estimate
            </span>
          ) : null}
        </div>
        {usingBonusNow ? (
          <p className="usage-bonus-active">
            This month’s plan credits are used up. You’re using your bonus credits now.
          </p>
        ) : null}
        {hasCreditBuckets ? (
          <div className="usage-bucket-grid">
            <section className="usage-bucket" aria-label="This month’s plan">
              <h3 className="usage-bucket-title">This month’s plan</h3>
              {hasAiAllowance ? (
                <>
                  <p className="usage-bucket-amounts">
                    <strong>{formatRetailUsd(nanoUsdToNumber(ai?.usedNanoUsd))} used</strong>
                    <span>
                      {formatRetailUsd(nanoUsdToNumber(ai?.remainingNanoUsd))} left of{' '}
                      {formatRetailUsd(aiAllowanceNanoUsd)}
                    </span>
                  </p>
                  <div
                    className="usage-progress"
                    role="progressbar"
                    aria-label="Monthly AI credits used"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={Math.round(aiPercentage)}
                  >
                    <span style={{ width: `${aiPercentage}%` }} />
                  </div>
                  <p className="usage-bucket-note">
                    {Math.round(aiPercentage)}% used · Resets {resetLabel}
                  </p>
                </>
              ) : (
                <p className="usage-bucket-note">Your plan has no monthly AI credits.</p>
              )}
            </section>
            <section className="usage-bucket usage-bucket--bonus" aria-label="Bonus credits">
              <h3 className="usage-bucket-title">
                Bonus credits <span className="usage-bucket-badge">Never expire</span>
              </h3>
              {bonusEverGranted ? (
                <>
                  <p className="usage-bucket-amounts">
                    <strong>{formatRetailUsd(bonusRemaining)} left</strong>
                  </p>
                  <ul className="usage-bonus-sources">
                    {bonusBySource
                      .filter((source) => source.granted > 0 || source.remaining > 0)
                      .map((source) => (
                        <li key={source.category}>
                          <span>{BONUS_LABELS[source.category]}</span>
                          <span>
                            {formatRetailUsd(source.remaining)} left
                            {source.granted > 0 ? ` of ${formatRetailUsd(source.granted)}` : ''}
                          </span>
                        </li>
                      ))}
                  </ul>
                  {grants.length > 0 ? (
                    <ul className="usage-bonus-grants" aria-label="Each bonus credit">
                      {grants.slice(0, 6).map((grant) => (
                        <li key={grant.id}>
                          <span>
                            {BONUS_LABELS[grant.category]} · {formatDate(grant.createdAt)}
                          </span>
                          <span>
                            {Number(grant.remainingUsd) < Number(grant.grantedUsd)
                              ? `${formatRetailUsd(Number(grant.remainingUsd))} left of ${formatRetailUsd(Number(grant.grantedUsd))}`
                              : formatRetailUsd(Number(grant.grantedUsd))}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </>
              ) : (
                <p className="usage-bucket-note">
                  No bonus credits yet. Credits you buy never expire.
                </p>
              )}
            </section>
          </div>
        ) : (
          <p className="usage-empty">
            No monthly credit allowance is attached to this entitlement. Recorded calls are still
            shown here.
          </p>
        )}
        {summary?.upgrade &&
        (!summary.upgrade.terminal || isAiExhausted || backtestPercentage >= 100) ? (
          <div className="usage-upgrade-offer">
            <UpgradeOfferSummary
              compact
              hideCurrentPlan={isAiExhausted}
              currentAiAllowanceUsd={aiAllowanceNanoUsd}
              offer={{
                ...summary.upgrade,
                resetDate: summary.upgrade.resetDate ?? summary.cycleEndDate,
              }}
              reason={
                summary.upgrade.terminal
                  ? 'You’ve reached your included allowance.'
                  : backtestPercentage >= 100
                    ? 'You’re out of backtest time.'
                    : undefined
              }
              onUpgrade={() =>
                summary.upgrade?.checkoutUrl &&
                navigateToCheckoutOverlay(navigate, summary.upgrade.checkoutUrl, location)
              }
            />
            {isAiExhausted && !summary.upgrade.terminal && (
              <p className="usage-reset-alternative">You can also wait for the reset.</p>
            )}
          </div>
        ) : summary?.upgrade?.terminal ? (
          <p className="usage-cycle-note">
            Current plan: {summary.upgrade.currentTier?.productName || 'your highest plan'}. You’re
            on our highest plan. No upgrade is needed.
          </p>
        ) : null}
        {hasAiAllowance && (
          <details className="usage-breakdown">
            <summary>Usage breakdown</summary>
            {showRetailDebug && (
              <p className="usage-credit-debug">
                {formatUsd(Number(ai?.display?.usedUsd))} of{' '}
                {formatUsd(Number(ai?.display?.allowanceUsd))} · {formatNumber(ai?.eventCount)}{' '}
                tracked calls
              </p>
            )}
            <div className="usage-source-grid">
              <div>
                <UsageSourceLabel
                  source="agent"
                  open={openSourceInfo === 'agent'}
                  onToggle={toggleSourceInfo}
                />
                <strong>{sourceDisplay(ai?.bySourceNanoUsd?.agent)}</strong>
              </div>
              <div>
                <UsageSourceLabel
                  source="strategy"
                  open={openSourceInfo === 'strategy'}
                  onToggle={toggleSourceInfo}
                />
                <strong>{sourceDisplay(ai?.bySourceNanoUsd?.strategy)}</strong>
              </div>
            </div>
          </details>
        )}
      </section>

      <section className="usage-credit-card usage-buy-credits" aria-label="Buy AI credits">
        <div className="usage-credit-heading">
          <div>
            <span className="usage-metric-label">Buy AI credits</span>
            <strong className="usage-credit-percent usage-buy-title">Need more AI time?</strong>
            <span className="usage-credit-balance">
              Pay once. Credits you buy never expire and stay after your plan resets.
            </span>
          </div>
        </div>
        {buyError ? (
          <Alert variant="danger" className="usage-settings-error">
            {buyError}
          </Alert>
        ) : null}
        {packsError ? (
          <p className="usage-empty">Credit packs can’t load right now.</p>
        ) : packs == null ? (
          <p className="usage-empty">Loading credit packs.</p>
        ) : packs.length === 0 ? (
          <p className="usage-empty">No credit packs are for sale right now.</p>
        ) : (
          <div className="usage-pack-grid">
            {packs.map((pack) => (
              <div className="usage-pack" key={pack.slug}>
                <strong className="usage-pack-credit">
                  {formatRetailUsd(Number(pack.creditUsd))} of AI credit
                </strong>
                <span className="usage-pack-note">Never expires</span>
                {pack.available && pack.priceCents != null ? (
                  <Button
                    type="button"
                    size="sm"
                    data-pack={pack.slug}
                    disabled={buyingSlug !== null}
                    onClick={() => {
                      void buyPack(pack);
                    }}
                  >
                    {buyingSlug === pack.slug
                      ? 'Opening Checkout'
                      : `Buy for ${formatPackPrice(pack.priceCents, pack.currency)}`}
                  </Button>
                ) : (
                  <span className="usage-pack-note">Not for sale right now</span>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="usage-credit-card" aria-label="Backtest usage">
        <div className="usage-credit-heading">
          <div>
            <span className="usage-metric-label">Backtest minutes</span>
            <strong className="usage-credit-percent">
              {formatNumber(usage?.backtestMinutesUsed)} /{' '}
              {formatLimit(limits?.maxBacktestMinutesPerMonth)}
            </strong>
          </div>
          <span className="usage-credit-debug">{Math.round(backtestPercentage)}% used</span>
        </div>
        <div
          className="usage-progress usage-progress-backtest"
          role="progressbar"
          aria-label="Monthly backtest minutes used"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(backtestPercentage)}
        >
          <span style={{ width: `${backtestPercentage}%` }} />
        </div>
      </section>

      <section className="usage-credit-card" aria-label="Live bot runtime usage">
        <div className="usage-credit-heading">
          <div>
            <span className="usage-metric-label">Live bot runtime</span>
            <strong className="usage-credit-percent">
              {formatNumber(usage?.deploymentRuntimeMinutesUsed)} minutes
            </strong>
            <span className="usage-credit-balance">
              Measured only. No runtime limit is applied.
            </span>
          </div>
        </div>
      </section>

      <details className="usage-technical-details">
        <summary>Technical details</summary>
        <dl className="usage-token-list">
          <div>
            <dt>Input tokens</dt>
            <dd>{formatNumber(ai?.inputTokens)}</dd>
          </div>
          <div>
            <dt>Output tokens</dt>
            <dd>{formatNumber(ai?.outputTokens)}</dd>
          </div>
          <div>
            <dt>Provider calls</dt>
            <dd>{formatNumber(ai?.eventCount)}</dd>
          </div>
        </dl>
        <h3 className="usage-section-title">By model</h3>
        {!ai?.byModel?.length ? (
          <p className="usage-empty">No AI usage recorded in this cycle yet.</p>
        ) : (
          <div className="usage-model-table-wrap">
            <table className="usage-model-table">
              <thead>
                <tr>
                  <th scope="col">Model</th>
                  <th scope="col">Events</th>
                  <th scope="col">Input</th>
                  <th scope="col">Output</th>
                  <th scope="col">Cached</th>
                  <th scope="col">Provider est.</th>
                </tr>
              </thead>
              <tbody>
                {ai.byModel.map((row) => (
                  <tr key={row.model}>
                    <td>{row.model}</td>
                    <td>{formatNumber(row.events)}</td>
                    <td>{formatNumber(row.inputTokens)}</td>
                    <td>{formatNumber(row.outputTokens)}</td>
                    <td>{formatNumber(row.cachedInputTokens)}</td>
                    <td>{formatUsd(row.estimatedCostUsd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </details>

      <p className="usage-cycle-note">
        Cycle {formatDate(summary?.cycleStartDate ?? null)} to{' '}
        {formatDate(summary?.cycleEndDate ?? null)}. AI credits include BotSpot managed Agent and
        strategy calls. BYOK calls are not charged.
      </p>
    </div>
  );
};

export default UsageSettings;
